import {
  ChangeDetectionStrategy,
  Component,
  signal,
  output,
  ElementRef,
  inject,
  OnDestroy,
  ChangeDetectorRef,
  ContentChild,
  TemplateRef,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';

/**
 * Dropdown menu component for displaying action menus.
 *
 * Unlike the form-based Dropdown component, DropdownMenu is designed for
 * navigation and action menus (e.g., user profile menus, context menus).
 *
 * This is a flexible container - you have full control over the content
 * and click handlers. Use template reference (#menu) to call menu.close().
 *
 * @example
 * ```html
 * <zag-dropdown-menu #menu>
 *   <ng-template #trigger>
 *     <button>Open Menu</button>
 *   </ng-template>
 *
 *   <div class="menu-item">Display-only item</div>
 *   <button (click)="doAction(); menu.close()">Action Button</button>
 *   <a href="/profile" (click)="menu.close()">Profile Link</a>
 * </zag-dropdown-menu>
 * ```
 */
@Component({
  selector: 'zag-dropdown-menu',
  imports: [NgTemplateOutlet],
  templateUrl: './dropdown-menu.html',
  styleUrl: './dropdown-menu.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'zag-dropdown-menu',
    '[class.zag-dropdown-menu--open]': 'isOpen()',
    '(keydown)': 'onKeyDown($event)',
  },
})
export class DropdownMenu implements OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly elementRef = inject(ElementRef);

  /** Reference to the trigger template */
  @ContentChild('trigger', { read: TemplateRef })
  triggerTemplate?: TemplateRef<unknown>;

  /** Emitted when the menu opens */
  readonly opened = output<void>();

  /** Emitted when the menu closes */
  readonly closed = output<void>();

  /** Internal state: whether menu is open */
  readonly isOpen = signal<boolean>(false);

  /** Internal state: currently highlighted item index */
  private readonly _highlightedIndex = signal<number>(-1);

  /** Exposed for child DropdownMenuItem components to track highlight state */
  readonly highlightedIndex = this._highlightedIndex.asReadonly();

  /** Click outside listener */
  private clickOutsideHandler: ((event: MouseEvent) => void) | null = null;

  /** Menu items (populated by child components) */
  private menuItems: HTMLElement[] = [];

  /** Flag to track if component is destroyed */
  private isDestroyed = false;

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.removeClickOutsideListener();
  }

  /**
   * Toggle menu open/closed state
   */
  toggle(): void {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Open the menu
   */
  open(): void {
    if (this.isOpen()) return;

    this.isOpen.set(true);
    this._highlightedIndex.set(-1);
    this.opened.emit();
    this.addClickOutsideListener();
    this.updateMenuItems();
    this.cdr.markForCheck();
  }

  /**
   * Close the menu (public API - can be called from template)
   */
  close(): void {
    if (!this.isOpen() || this.isDestroyed) return;

    this.isOpen.set(false);
    this._highlightedIndex.set(-1);
    this.closed.emit();
    this.removeClickOutsideListener();
    this.cdr.markForCheck();
  }

  /**
   * Handle keyboard navigation
   */
  onKeyDown(event: KeyboardEvent): void {
    if (!this.isOpen()) {
      // If closed and user presses Enter/Space on trigger, open menu
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.open();
      }
      return;
    }

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.close();
        break;

      case 'ArrowDown':
        event.preventDefault();
        this.highlightNext();
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.highlightPrevious();
        break;

      case 'Enter':
      case ' ':
        event.preventDefault();
        this.activateHighlighted();
        break;

      case 'Home':
        event.preventDefault();
        this.highlightFirst();
        break;

      case 'End':
        event.preventDefault();
        this.highlightLast();
        break;

      case 'Tab':
        this.close();
        break;
    }
  }

  /**
   * Get current highlighted index
   * @deprecated Use the `highlightedIndex` signal directly instead
   */
  getHighlightedIndex(): number {
    return this._highlightedIndex();
  }

  /**
   * Set highlighted index
   */
  setHighlightedIndex(index: number): void {
    this._highlightedIndex.set(index);
  }

  /**
   * Update menu items list from DOM
   */
  private updateMenuItems(): void {
    setTimeout(() => {
      const menuContent = this.elementRef.nativeElement.querySelector('.zag-dropdown-menu__content');
      if (menuContent) {
        this.menuItems = Array.from(menuContent.querySelectorAll('zag-dropdown-menu-item'));
      }
    }, 0);
  }

  /**
   * Highlight next enabled item
   */
  private highlightNext(): void {
    const items = this.menuItems;
    if (items.length === 0) return;

    let index = this._highlightedIndex();
    const startIndex = index;

    do {
      index = (index + 1) % items.length;
      const item = items[index];
      if (!item.hasAttribute('disabled')) {
        this._highlightedIndex.set(index);
        this.scrollItemIntoView(item);
        return;
      }
    } while (index !== startIndex);
  }

  /**
   * Highlight previous enabled item
   */
  private highlightPrevious(): void {
    const items = this.menuItems;
    if (items.length === 0) return;

    let index = this._highlightedIndex();
    const startIndex = index;

    do {
      index = index <= 0 ? items.length - 1 : index - 1;
      const item = items[index];
      if (!item.hasAttribute('disabled')) {
        this._highlightedIndex.set(index);
        this.scrollItemIntoView(item);
        return;
      }
    } while (index !== startIndex);
  }

  /**
   * Highlight first enabled item
   */
  private highlightFirst(): void {
    const items = this.menuItems;
    for (let i = 0; i < items.length; i++) {
      if (!items[i].hasAttribute('disabled')) {
        this._highlightedIndex.set(i);
        this.scrollItemIntoView(items[i]);
        return;
      }
    }
  }

  /**
   * Highlight last enabled item
   */
  private highlightLast(): void {
    const items = this.menuItems;
    for (let i = items.length - 1; i >= 0; i--) {
      if (!items[i].hasAttribute('disabled')) {
        this._highlightedIndex.set(i);
        this.scrollItemIntoView(items[i]);
        return;
      }
    }
  }

  /**
   * Activate (click) the currently highlighted item
   */
  private activateHighlighted(): void {
    const index = this._highlightedIndex();
    if (index >= 0 && index < this.menuItems.length) {
      const item = this.menuItems[index];
      if (!item.hasAttribute('disabled')) {
        item.click();
      }
    }
  }

  /**
   * Scroll item into view if needed
   */
  private scrollItemIntoView(item: HTMLElement): void {
    item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  /**
   * Add click-outside listener to close menu
   */
  private addClickOutsideListener(): void {
    this.clickOutsideHandler = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!this.elementRef.nativeElement.contains(target)) {
        this.close();
      }
    };

    // Use setTimeout to avoid immediate trigger
    const handler = this.clickOutsideHandler;
    setTimeout(() => {
      if (handler) {
        document.addEventListener('click', handler);
      }
    }, 0);
  }

  /**
   * Remove click-outside listener
   */
  private removeClickOutsideListener(): void {
    if (this.clickOutsideHandler) {
      document.removeEventListener('click', this.clickOutsideHandler);
      this.clickOutsideHandler = null;
    }
  }
}
